import { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageTitle, Panel } from "../../components/app/Kit";

const STEPS = [
  ["Business name", "name", "text", "The name customers recognise."],
  ["Industry", "industry", "select", "Sets services, pipeline and AI instruction defaults."],
  ["Location / timezone", "timezone", "text", "Used for availability and reminder timing."],
  ["Services", "services", "textarea", "One per line. These become bookable appointment types."],
  ["Business hours", "business_hours", "text", "When your team is available to take work."],
  ["Calendar", "calendar", "provider", "Two-way appointment sync."],
  ["Phone / voice", "voice", "provider", "Inbound and outbound voice handling."],
  ["SMS / WhatsApp", "messaging", "provider", "Two-way messaging channels."],
  ["AI receptionist", "ai", "info", "Scope, approved answers and escalation."],
  ["Team", "team", "info", "Invite the people who will work the pipeline."],
  ["Automation preferences", "automation", "info", "Which workflows should be live at launch."],
  ["Launch checklist", "launch", "checklist", "Everything that must be true before going live."],
];

const INDUSTRIES = ["Dental", "HVAC", "Plumbing", "Roofing", "Electrician", "Cleaning", "Med Spa", "Law Firm", "Real Estate", "Auto Repair", "Salon", "Fitness", "Other"];

export default function Onboarding() {
  const { org, setOrg } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: org?.name || "",
    industry: org?.industry || "Dental",
    timezone: org?.timezone || "UTC",
    services: (org?.services || []).join("\n"),
    business_hours: org?.business_hours || "Mon–Fri, 09:00–17:00",
  });
  const [saving, setSaving] = useState(false);

  const [current, key, kind, hint] = STEPS[step];
  const progress = Math.round(((step + 1) / STEPS.length) * 100);

  const finish = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/organization", {
        name: form.name,
        industry: form.industry,
        timezone: form.timezone,
        business_hours: form.business_hours,
        services: form.services.split("\n").map((s) => s.trim()).filter(Boolean),
        onboarding_complete: true,
        onboarding_step: STEPS.length,
      });
      setOrg(data);
      toast.success("Setup saved. Your workspace is configured.");
      navigate("/app/dashboard");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="onboarding-page">
      <PageTitle
        testId="onboarding-title"
        title="Set up your operating system"
        sub="Twelve steps. You can revisit any of them later in Settings."
      />

      <div className="mb-5">
        <div className="flex items-center justify-between text-[11px] text-elv-dim">
          <span>
            Step {step + 1} of {STEPS.length}
          </span>
          <span data-testid="onboarding-progress">{progress}% complete</span>
        </div>
        <div className="mt-2 h-1.5 w-full rounded-full bg-white/[0.05]">
          <div
            className="h-1.5 rounded-full bg-gradient-to-r from-elv-green to-elv-cyan transition-[width] duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <Panel testId={`onboarding-step-${step}`} title={current}>
        <p className="text-xs text-elv-dim">{hint}</p>

        <div className="mt-5">
          {kind === "text" ? (
            <input
              value={form[key] || ""}
              onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
              data-testid={`onboarding-input-${key}`}
              className="w-full max-w-md rounded-md border border-elv-line bg-white/[0.02] px-3 py-2.5 text-sm text-elv-text outline-none focus:border-elv-green/40"
            />
          ) : null}

          {kind === "select" ? (
            <select
              value={form.industry}
              onChange={(e) => setForm((f) => ({ ...f, industry: e.target.value }))}
              data-testid="onboarding-input-industry"
              className="w-full max-w-md rounded-md border border-elv-line bg-[#0A0F14] px-3 py-2.5 text-sm text-elv-text outline-none"
            >
              {INDUSTRIES.map((i) => (
                <option key={i} value={i}>
                  {i}
                </option>
              ))}
            </select>
          ) : null}

          {kind === "textarea" ? (
            <textarea
              rows={5}
              value={form.services}
              onChange={(e) => setForm((f) => ({ ...f, services: e.target.value }))}
              data-testid="onboarding-input-services"
              className="w-full max-w-md rounded-md border border-elv-line bg-white/[0.02] p-3 text-sm text-elv-text outline-none focus:border-elv-green/40"
            />
          ) : null}

          {kind === "provider" ? (
            <div className="rounded-md border border-amber-300/25 bg-amber-300/[0.05] p-4 text-xs text-amber-200">
              <p className="font-medium">Connection required.</p>
              <p className="mt-1.5">
                This provider isn't connected yet. You can finish setup now and connect it from the Integrations
                page — the platform will keep reporting it as not connected until credentials exist.
              </p>
            </div>
          ) : null}

          {kind === "info" ? (
            <ul className="grid gap-2">
              {["Defaults are applied from your industry template.", "You can refine everything later without re-running setup.", "Nothing goes live until you complete the launch checklist."].map((i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-elv-dim">
                  <Icons.Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-elv-green" />
                  {i}
                </li>
              ))}
            </ul>
          ) : null}

          {kind === "checklist" ? (
            <ul className="grid gap-2">
              {[
                ["Business profile completed", !!form.name],
                ["Industry selected", !!form.industry],
                ["Services defined", form.services.trim().length > 0],
                ["Business hours set", !!form.business_hours],
                ["Calendar provider connected", false],
                ["Voice provider connected", false],
                ["Messaging provider connected", false],
                ["Payments provider connected", false],
              ].map(([label, done]) => (
                <li key={label} data-testid={`launch-check-${label.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-center justify-between rounded-md border border-white/[0.06] bg-white/[0.02] p-3 text-xs">
                  <span className="text-elv-text">{label}</span>
                  {done ? (
                    <span className="flex items-center gap-1.5 text-elv-green">
                      <Icons.CheckCircle2 className="h-3.5 w-3.5" /> Ready
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-amber-200">
                      <Icons.AlertCircle className="h-3.5 w-3.5" /> Connection required
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <div className="mt-7 flex flex-wrap items-center gap-3 border-t border-elv-line pt-5">
          <button
            type="button"
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            data-testid="onboarding-back"
            className="rounded-md border border-elv-line px-4 py-2 text-xs text-elv-dim hover:text-elv-text disabled:opacity-40"
          >
            Back
          </button>
          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={() => setStep((s) => s + 1)}
              data-testid="onboarding-next"
              className="rounded-md bg-elv-green px-4 py-2 text-xs font-semibold text-[#04120A]"
            >
              Continue
            </button>
          ) : (
            <button
              type="button"
              onClick={finish}
              disabled={saving}
              data-testid="onboarding-finish"
              className="inline-flex items-center gap-2 rounded-md bg-elv-green px-4 py-2 text-xs font-semibold text-[#04120A] disabled:opacity-60"
            >
              {saving ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Finish setup
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate("/app/dashboard")}
            data-testid="onboarding-skip"
            className="text-xs text-elv-dim underline underline-offset-4 hover:text-elv-text"
          >
            Skip for now
          </button>
        </div>
      </Panel>
    </div>
  );
}
